import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { IconButton } from '../Button';
import { XMarkIcon } from '../Icons/solid';
import { cn } from '../utils/classes';
import { type Transition as TransitionType, transitions } from './config/transitions';

/**
 * Primitive — for use inside arch-ui only. Applications must use `arch-ui/src/Modal`,
 * which composes these parts with the standard responsive chrome. See `./index.ts`.
 */

/**
 * When true, marks this modal's portal container as `inert`,
 * prevents outside-click dismissal, and disables the escape key handler.
 */
export const InertModalContext = React.createContext(false);

/**
 * Context for the modal content element.
 * It is meant to be used to scope portals to prevent scroll/focus trap issues.
 * @see {../Combobox/Combobox.tsx} for example usage
 */
export const ModalContentContext = React.createContext<HTMLElement | undefined>(undefined);

export type MaxWidth =
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | 'none'
  | '1/2'
  | '1/3'
  | '2/3'
  | '1/4'
  | '3/4'
  | '1/5'
  | '2/5'
  | '3/5'
  | '4/5'
  | '1/6'
  | '5/6'
  | 'full';

const maxWithOptions = {
  xs: 'max-w-xs',
  sm: 'w-[25rem]', // 400px
  md: 'w-[32rem]', // 512px
  lg: 'w-[50rem]', // 800px
  xl: 'max-w-144',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  none: 'max-w-none',

  /**
   * Tailwinds percentage based width options
   * Reference: https://tailwindcss.com/docs/width#using-a-percentage
   */
  '1/2': 'max-w-1/2',
  '1/3': 'max-w-1/3',
  '2/3': 'max-w-2/3',
  '1/4': 'max-w-1/4',
  '3/4': 'max-w-3/4',
  '1/5': 'max-w-1/5',
  '2/5': 'max-w-2/5',
  '3/5': 'max-w-3/5',
  '4/5': 'max-w-4/5',
  '1/6': 'max-w-1/6',
  '5/6': 'max-w-5/6',
  full: 'max-w-full',
} as { [key in MaxWidth]: string };

const getMaxWidth = (maxWidth: MaxWidth) => maxWithOptions[maxWidth];

export type ModalBaseRootProps = React.PropsWithChildren<{
  /**
   * Root component classname
   */
  className?: string;
  /**
   * Cypress test selector
   */
  'data-cy'?: string;
  /**
   * If true, will not trigger onClose.
   */
  disableEscapeKey?: boolean;
  /**
   * Called when the Dialog is dismissed (via the overlay or Escape key). Typically used to close the dialog by setting open to false.
   */
  onClose: (value?: boolean) => void;
  /**
   * Set modal fullscreen
   */
  fullScreen?: boolean;
  /**
   * Set modal full width
   */
  fullWidth?: boolean;
  /**
   * Determine the max-width of the dialog.
   */
  maxWidth?: MaxWidth;
  /**
   * Adds an outside pad around the modal. Useful when used, for example, with fullScreen set to true.
   */
  outsidePad?: boolean;
  /**
   * Adds rounded borders. Usually used when not in fullscreen and without outsidePad
   */
  roundBorders?: boolean;
  /**
   * Type of transitions to be applied to the modal. When auto, it will apply slide-bottom when small screen and fade for larger screens
   */
  transition?: TransitionType;
  /**
   * Override z-index of the modal
   */
  zIndex?: number;
  /**
   * Whether the dialog is open
   */
  open?: boolean;
  /**
   * Ref to an element that should receive focus when the dialog opens
   */
  initialFocus?: React.RefObject<HTMLElement | null>;
}>;

export const ModalBaseRoot = ({
  children,
  className,
  'data-cy': dataCy,
  disableEscapeKey,
  fullScreen,
  fullWidth,
  maxWidth,
  outsidePad,
  roundBorders,
  zIndex,
  transition = 'none',
  onClose,
  open,
  initialFocus,
}: ModalBaseRootProps) => {
  const hasOverlay = !fullScreen || outsidePad;
  const inertModal = useContext(InertModalContext);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !inertModal) {
      onClose();
    }
  };

  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    if (disableEscapeKey || inertModal) {
      event.preventDefault();
    }
  };

  // Only close when clicking the overlay (inside our modal portal).
  // Prevent closing when clicking portaled content outside the modal
  // (e.g., DatePicker calendars, Select panels).
  const handleOutsideInteraction = (event: CustomEvent) => {
    if (inertModal) {
      event.preventDefault();
      return;
    }
    const target = event.target as Element;
    if (!target.closest('[data-modal-portal]')) {
      event.preventDefault();
    }
  };

  const handleOpenAutoFocus = (event: Event) => {
    if (inertModal) {
      event.preventDefault();
    } else if (initialFocus?.current) {
      event.preventDefault();
      initialFocus.current.focus();
    }
  };

  useLayoutEffect(() => {
    return () => {
      const root = document.getElementById('root');
      if (root) requestAnimationFrame(() => root.removeAttribute('aria-hidden'));
    };
  }, []);

  // Radix Dialog sets pointer-events: none on body which blocks content
  // rendered via createPortal outside the dialog (e.g., DatePicker calendars,
  // Select panels). Remove it so portaled content remains interactive.
  // Our full-viewport overlay already prevents interaction with background content.
  // Note: modal={false} was considered but rejected - it disables focus trapping,
  // overlay rendering, and outside-click blocking, which are all required.
  useEffect(() => {
    if (!open) return;

    const clearBodyPointerEventsLock = () => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.removeProperty('pointer-events');
      }
    };

    const observer = new MutationObserver(clearBodyPointerEventsLock);
    observer.observe(document.body, { attributeFilter: ['style'], attributes: true });
    // On reopen, Radix's DismissableLayer effect runs in the child render
    // before this effect re-attaches its observer, so the lock is already in
    // place by the time we start observing. Clear it explicitly here.
    clearBodyPointerEventsLock();

    return () => {
      observer.disconnect();
      document.body.style.removeProperty('pointer-events');
    };
  }, [open]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <div
          data-modal-portal=""
          inert={inertModal || undefined}
          style={{ zIndex }}
          className={cn('fixed inset-0 z-modal flex items-center justify-center', { 'p-8': outsidePad })}
        >
          {hasOverlay && (
            <DialogPrimitive.Overlay asChild>
              <ModalBaseOverlay />
            </DialogPrimitive.Overlay>
          )}
          <DialogPrimitive.Content
            ref={setContentElement}
            aria-describedby={undefined}
            data-cy={dataCy}
            onEscapeKeyDown={handleEscapeKeyDown}
            onPointerDownOutside={handleOutsideInteraction}
            onInteractOutside={handleOutsideInteraction}
            onOpenAutoFocus={handleOpenAutoFocus}
            style={{ pointerEvents: 'auto' }}
            className={cn(
              'flex flex-col max-h-full',
              transitions[transition],
              {
                'w-full': fullWidth,
                'h-full w-full': fullScreen,
              },
              maxWidth && getMaxWidth(maxWidth),
              className as string
            )}
          >
            <ModalContentContext.Provider value={contentElement ?? undefined}>
              <div
                className={cn('flex grow transform flex-col overflow-hidden z-modal bg-white', {
                  'shadow-xl': outsidePad,
                  'rounded-lg': roundBorders,
                })}
              >
                {children}
              </div>
            </ModalContentContext.Provider>
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export type ModalBaseCloseButtonProps = {
  onClose: () => void;
};
export const ModalBaseCloseButton: React.FC<ModalBaseCloseButtonProps> = ({ onClose }) => {
  return <IconButton icon={XMarkIcon} onClick={onClose} size="sm" color="secondary" variant="text" />;
};

export type ModalBaseContentProps = React.HtmlHTMLAttributes<HTMLDivElement>;
export const ModalBaseContent: React.FC<ModalBaseContentProps> = ({ className, ...props }) => {
  return (
    <div
      data-testid="modal-content"
      className={cn('flex h-full grow flex-col overflow-auto px-6', className)}
      {...props}
    />
  );
};

export interface ModalBaseFooterProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
  /**
   * If the footer should render a top border line.
   */
  topBorder?: boolean;
}
export const ModalBaseFooter: React.FC<ModalBaseFooterProps> = ({ className, topBorder = false, ...props }) => {
  return (
    <div
      data-testid="modal-footer"
      {...props}
      className={cn(
        'flex w-full items-center justify-end space-x-2 px-5 pt-4 pb-6 sm:pb-4',
        topBorder && 'border-t border-gray-200',
        className
      )}
    />
  );
};

const ModalBaseCloseIcon: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ onClick }) => (
  <button
    type="button"
    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
    onClick={onClick}
    aria-label="Close Overlay"
  >
    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
  </button>
);

export interface ModalBaseHeaderProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
  /**
   * If the header should render a bottom border line.
   */
  bottomBorder?: boolean;
  /**
   * Callback called when clicked on the close icon button. This prop manage if renders or not the close icon component.
   */
  onClose?: () => void;
  /**
   * Renders content (button | text | icons etc) right before the close icon button
   */
  rightContent?: React.ReactNode;
}
export const ModalBaseHeader: React.FC<ModalBaseHeaderProps> = ({
  bottomBorder = false,
  className,
  children,
  onClose,
  rightContent,
  ...props
}) => {
  return (
    <div
      data-testid="modal-header"
      className={cn(
        'relative w-full flex flex-col py-5 px-4 gap-2',
        {
          'pr-16': onClose,
          'border-b border-gray-200': bottomBorder,
        },
        className
      )}
      {...props}
    >
      {children}
      <div className="absolute flex items-center gap-3 right-5 top-4">
        {rightContent}
        {onClose && <ModalBaseCloseIcon onClick={onClose} />}
      </div>
    </div>
  );
};

export interface ModalBaseOverlayProps extends React.HtmlHTMLAttributes<HTMLDivElement> {}

export const ModalBaseOverlay: React.FC<ModalBaseOverlayProps> = ({ className, ...props }) => {
  return <div {...props} aria-hidden="true" className={cn('fixed inset-0 bg-black opacity-30', className)} />;
};

export type ModalBaseTitleProps = React.ComponentPropsWithRef<typeof DialogPrimitive.Title>;
export const ModalBaseTitle = ({ className, ...props }: ModalBaseTitleProps) => (
  <DialogPrimitive.Title className={cn('text-lg font-medium text-gray-900 leading-6', className)} {...props} />
);

export type ModalBaseSubTitleProps = React.HtmlHTMLAttributes<HTMLHeadingElement>;
export const ModalBaseSubTitle = ({ className, ...props }: ModalBaseSubTitleProps) => (
  <DialogPrimitive.Description asChild>
    <h3 className={cn('text-sm font-normal leading-5 text-gray-500', className)} {...props} />
  </DialogPrimitive.Description>
);
